import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBigUp, MessageCircle, GitCompareArrows, Volume2, VolumeX, Play, Activity, Trash2, Loader2, Flame, ScanLine, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatDate, scoreBg, videoStreamUrl, videoThumbUrl } from '../lib/api';
import { useLanguage } from '../i18n';
import { useDominantColor } from '../lib/useDominantColor';
import { AmbientGlow } from '../components/AmbientGlow';
import { UserAvatar } from '../components/UserAvatar';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const PitchItem = ({ post, muted, onMuteToggle, onUpvote, onOpenComments, onCompare, onDelete, lang, t }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [variant, setVariant] = useState('original');
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(false);
  const thumbUrl = post.has_thumbnail ? videoThumbUrl(post.analysis_id) : null;
  const glowRgb = useDominantColor(thumbUrl);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setActive(entry.intersectionRatio >= 0.6);
        });
      },
      { threshold: [0, 0.6, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      v.play().catch(() => { /* autoplay blocked by browser policy - intentional no-op */ });
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [active, variant]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => { /* autoplay blocked by browser policy - intentional no-op */ });
    else v.pause();
  };

  const score = post.report?.overall_score;
  const moveLabel = lang === 'it' ? post.report?.move_label_it : post.report?.move_label_en;

  return (
    <div ref={containerRef} className="relative h-full w-full flex items-center justify-center overflow-hidden" data-testid={`pitch-post-${post.post_id}`}>
      <AmbientGlow rgb={glowRgb} intensity={0.3} />

      <div className="relative h-full w-full max-w-md mx-auto flex items-center justify-center px-3 py-2">
        <div className="relative w-full h-full rounded-[20px] overflow-hidden bg-black/70 border border-white/[0.06]">
          <video
            key={variant}
            ref={videoRef}
            src={videoStreamUrl(post.analysis_id, variant)}
            playsInline
            muted={muted}
            loop
            className="w-full h-full object-contain"
            data-testid={`pitch-video-${post.post_id}`}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onClick={togglePlay}
          />
          {!playing && (
            <button onClick={togglePlay} data-testid={`pitch-play-${post.post_id}`} className="absolute inset-0 flex items-center justify-center bg-black/25">
              <span className="w-16 h-16 rounded-[20px] bg-[#22C55E] flex items-center justify-center shadow-[0_0_40px_rgba(34,197,94,0.4)]">
                <Play size={26} className="text-[#06210F] ml-1" />
              </span>
            </button>
          )}

          {/* bottom gradient for legibility */}
          <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.75))' }} />

          {/* author + caption */}
          <div className="absolute left-3 right-16 bottom-3 space-y-1.5">
            <div className="flex items-center gap-2">
              <UserAvatar src={post.author?.picture} alt={post.author?.name} size={32} rounded={12} />
              <span className="text-sm font-semibold text-white truncate" data-testid={`pitch-author-${post.post_id}`}>{post.author?.name}</span>
              {post.author?.role === 'coach' && (
                <span className="font-mono text-[9px] uppercase text-white bg-white/[0.15] border border-white/20 rounded-[8px] px-1.5 py-px">Coach</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {moveLabel && <span className="font-mono text-[11px] text-white/85 bg-black/50 border border-white/15 rounded-[10px] px-2 py-0.5">{moveLabel}</span>}
              {score != null && (
                <span className={`border rounded-[10px] px-2 py-0.5 font-mono text-xs font-medium tabular-nums ${scoreBg(score)}`} data-testid={`pitch-score-${post.post_id}`}>
                  {score}
                </span>
              )}
              <span className="font-mono text-[10px] text-white/45">{formatDate(post.created_at, lang)}</span>
            </div>
            {post.caption && <p className="text-sm text-white/85 leading-snug line-clamp-2" data-testid={`pitch-caption-${post.post_id}`}>{post.caption}</p>}
          </div>

          {/* right action rail */}
          <div className="absolute right-2.5 bottom-3 flex flex-col items-center gap-3">
            <div className="flex flex-col items-center gap-0.5">
              <button
                onClick={() => onUpvote(post)}
                data-testid={`pitch-upvote-${post.post_id}`}
                className={`w-11 h-11 rounded-[16px] border flex items-center justify-center transition-colors ${
                  post.upvoted_by_me ? 'bg-white text-[#121212] border-white' : 'bg-black/45 border-white/15 text-white hover:bg-black/60'
                }`}
                aria-label={t('upvote')}
              >
                <ArrowBigUp size={20} className={post.upvoted_by_me ? 'fill-[#121212]' : ''} />
              </button>
              <span className="font-mono text-[11px] text-white/85 tabular-nums" data-testid={`pitch-upvote-count-${post.post_id}`}>{post.upvotes_count}</span>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <button
                onClick={() => onOpenComments(post)}
                data-testid={`pitch-comments-${post.post_id}`}
                className="w-11 h-11 rounded-[16px] bg-black/45 border border-white/15 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
                aria-label={t('comments')}
              >
                <MessageCircle size={19} />
              </button>
              <span className="font-mono text-[11px] text-white/85 tabular-nums">{post.comments_count}</span>
            </div>
            <button
              onClick={() => onCompare(post)}
              data-testid={`pitch-compare-${post.post_id}`}
              className="w-11 h-11 rounded-[16px] bg-[#22C55E] text-[#06210F] flex items-center justify-center shadow-[0_6px_18px_rgba(34,197,94,0.35)] hover:bg-[#4ADE80] transition-colors"
              aria-label={t('compare')}
            >
              <GitCompareArrows size={19} />
            </button>
            {post.has_overlay && (
              <button
                onClick={() => setVariant((v) => (v === 'overlay' ? 'original' : 'overlay'))}
                data-testid={`pitch-overlay-toggle-${post.post_id}`}
                className={`w-11 h-11 rounded-[16px] border flex items-center justify-center transition-colors ${
                  variant === 'overlay' ? 'bg-white text-[#121212] border-white' : 'bg-black/45 border-white/15 text-white hover:bg-black/60'
                }`}
                aria-label={t('skeletonVideo')}
              >
                <Activity size={18} />
              </button>
            )}
            <button
              onClick={onMuteToggle}
              data-testid={`pitch-mute-${post.post_id}`}
              className="w-11 h-11 rounded-[16px] bg-black/45 border border-white/15 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
              aria-label="mute"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            {post.is_mine && (
              <button
                onClick={() => onDelete(post)}
                data-testid={`pitch-delete-${post.post_id}`}
                className="w-11 h-11 rounded-[16px] bg-black/45 border border-white/15 text-white/70 flex items-center justify-center hover:text-[#E76F7B] transition-colors"
                aria-label={t('deletePost')}
              >
                <Trash2 size={17} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Pitch() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [muted, setMuted] = useState(true);
  const [commentsPost, setCommentsPost] = useState(null);
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const containerRef = useRef(null);

  const fetchFeed = useCallback(async (before) => {
    const { data } = await api.get('/pitch/feed', { params: { limit: 10, before: before || undefined } });
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchFeed();
        setPosts(data.items);
        setNextBefore(data.next_before);
      } catch (err) {
        console.error('Pitch feed load failed:', err);
        setPosts([]);
      }
    })();
  }, [fetchFeed]);

  const loadMore = useCallback(async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchFeed(nextBefore);
      setPosts((prev) => [...prev, ...data.items]);
      setNextBefore(data.next_before);
    } catch (err) {
      console.warn('Pitch loadMore failed (will retry on next scroll):', err);
    } finally {
      setLoadingMore(false);
    }
  }, [nextBefore, loadingMore, fetchFeed]);

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight * 1.5) loadMore();
  };

  const handleUpvote = async (post) => {
    // optimistic
    setPosts((prev) => prev.map((p) => (p.post_id === post.post_id
      ? { ...p, upvoted_by_me: !p.upvoted_by_me, upvotes_count: p.upvotes_count + (p.upvoted_by_me ? -1 : 1) }
      : p)));
    try {
      const { data } = await api.post(`/pitch/posts/${post.post_id}/upvote`);
      setPosts((prev) => prev.map((p) => (p.post_id === post.post_id ? { ...p, upvoted_by_me: data.upvoted, upvotes_count: data.upvotes_count } : p)));
    } catch (err) {
      console.error('Upvote toggle failed, reverting optimistic update:', err);
      setPosts((prev) => prev.map((p) => (p.post_id === post.post_id ? { ...p, upvoted_by_me: post.upvoted_by_me, upvotes_count: post.upvotes_count } : p)));
    }
  };

  const openComments = async (post) => {
    setCommentsPost(post);
    setComments(null);
    try {
      const { data } = await api.get(`/pitch/posts/${post.post_id}/comments`);
      setComments(data.items);
    } catch (err) {
      console.error('Failed to load comments:', err);
      setComments([]);
    }
  };

  const sendComment = async () => {
    const text = commentText.trim();
    if (!text || !commentsPost) return;
    setSending(true);
    try {
      const { data } = await api.post(`/pitch/posts/${commentsPost.post_id}/comments`, { text });
      setComments((prev) => [...(prev || []), data]);
      setCommentText('');
      setPosts((prev) => prev.map((p) => (p.post_id === commentsPost.post_id ? { ...p, comments_count: p.comments_count + 1 } : p)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  const confirmDelete = async () => {
    const post = deleteTarget;
    setDeleteTarget(null);
    if (!post) return;
    try {
      await api.delete(`/pitch/posts/${post.post_id}`);
      setPosts((prev) => prev.filter((p) => p.post_id !== post.post_id));
      toast.success(t('deletePost') + ' \u2713');
    } catch (err) {
      console.error('Delete post failed:', err);
      toast.error('Error');
    }
  };

  if (posts === null) {
    return (
      <div className="h-[calc(100dvh-120px)] flex items-center justify-center" data-testid="pitch-loading">
        <div className="flex gap-2">
          <div className="ss-dot" />
          <div className="ss-dot" />
          <div className="ss-dot" />
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="h-[calc(100dvh-120px)] flex items-center justify-center px-6" data-testid="pitch-empty">
        <div className="ss-card p-10 text-center space-y-4 max-w-sm">
          <span className="inline-flex w-14 h-14 rounded-[16px] bg-white/[0.07] border border-white/12 items-center justify-center">
            <Flame size={24} className="text-white" />
          </span>
          <div>
            <h2 className="font-display font-bold text-white">{t('pitchEmpty')}</h2>
            <p className="text-xs text-white/45 font-mono mt-1">{t('pitchEmptySub')}</p>
          </div>
          <button onClick={() => navigate('/analyze')} data-testid="pitch-empty-analyze-cta" className="ss-btn-primary h-11 px-6 text-sm font-semibold inline-flex items-center gap-2">
            <ScanLine size={16} /> {t('analyzeNewSkill')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="pitch-page">
      <div
        ref={containerRef}
        onScroll={onScroll}
        className="pitch-snap h-[calc(100dvh-120px)] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="pitch-feed"
      >
        {posts.map((post) => (
          <div key={post.post_id} className="h-full">
            <PitchItem
              post={post}
              muted={muted}
              onMuteToggle={() => setMuted((m) => !m)}
              onUpvote={handleUpvote}
              onOpenComments={openComments}
              onCompare={(p) => navigate(`/pitch/compare?post=${p.post_id}`)}
              onDelete={setDeleteTarget}
              lang={lang}
              t={t}
            />
          </div>
        ))}
        {loadingMore && (
          <div className="py-4 flex justify-center">
            <Loader2 size={18} className="animate-spin text-white/50" />
          </div>
        )}
      </div>

      {/* Comments drawer */}
      <Drawer open={!!commentsPost} onOpenChange={(open) => !open && setCommentsPost(null)}>
        <DrawerContent className="bg-[#1E1E1E] border-white/10 rounded-t-[24px] max-h-[75dvh]" data-testid="pitch-comments-drawer">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="font-display text-white text-left">
              {t('comments')} <span className="font-mono text-xs text-white/45">({commentsPost?.comments_count ?? 0})</span>
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-2 overflow-y-auto flex-1 min-h-[180px] space-y-3">
            {comments === null ? (
              <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-white/50" /></div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-white/45 text-center py-8" data-testid="pitch-no-comments">{t('noComments')}</p>
            ) : (
              comments.map((c) => (
                <div key={c.comment_id} className="flex items-start gap-2.5" data-testid={`pitch-comment-${c.comment_id}`}>
                  <UserAvatar src={c.author?.picture} alt={c.author?.name} size={28} rounded={10} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-white truncate">{c.author?.name}</span>
                      {c.author?.role === 'coach' && (
                        <span className="font-mono text-[9px] uppercase text-white bg-white/[0.12] border border-white/15 rounded-[8px] px-1 py-px">Coach</span>
                      )}
                      <span className="font-mono text-[9px] text-white/35">{formatDate(c.created_at, lang)}</span>
                    </div>
                    <p className="text-sm text-white/80 leading-snug mt-0.5 break-words">{c.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="p-4 pt-2 border-t border-white/[0.07] flex items-center gap-2">
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendComment()}
              placeholder={t('writeComment')}
              maxLength={500}
              data-testid="pitch-comment-input"
              className="flex-1 h-11 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none"
            />
            <button
              onClick={sendComment}
              disabled={sending || !commentText.trim()}
              data-testid="pitch-comment-send"
              className="ss-btn-primary w-11 h-11 flex items-center justify-center disabled:opacity-40"
              aria-label={t('send')}
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#1E1E1E] border-white/10 rounded-[20px] max-w-sm" data-testid="pitch-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-white">{t('deletePost')}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/[0.06] border-white/10 rounded-[14px]" data-testid="pitch-delete-cancel">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="pitch-delete-confirm"
              className="bg-[rgba(231,111,123,0.14)] text-[#E76F7B] border border-[rgba(231,111,123,0.3)] rounded-[14px] hover:bg-[rgba(231,111,123,0.2)]"
            >
              {t('confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
